import { FormattedMessage } from 'react-intl';

import { Gap } from 'ui-components/components/flex';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';

import { InputLabel } from '../../../../../../../../../address/input-label';
import type { BarcodeFieldError } from '../barcode-field/barcode-field';
import { BarcodeField } from '../barcode-field/barcode-field';
import type { UomFormState } from '../../../../uom-types';
import { UOM_SKU_MAX_LENGTH } from '../../uom-modal.constants';
import { messages } from '../../uom-modal.messages';
import style from '../../uom-modal.module.css';

type IdentifiersSectionProps = {
  barcodeError: BarcodeFieldError;
  effectiveBarcode: string;
  form: Pick<UomFormState, 'barcode' | 'barcodeIsAuto' | 'uomSku'>;
  isDuplicateBarcode?: boolean;
  isDuplicateUomSku?: boolean;
  onBarcodeBlur: () => void;
  onBarcodeChange: (value: string) => void;
  onRegenerate: () => void;
  onToggleManual: () => void;
  onUomSkuChange: (value: string) => void;
  onUseAuto: () => void;
};

export const IdentifiersSection = ({
  barcodeError,
  effectiveBarcode,
  form,
  isDuplicateBarcode,
  isDuplicateUomSku,
  onBarcodeBlur,
  onBarcodeChange,
  onRegenerate,
  onToggleManual,
  onUomSkuChange,
  onUseAuto,
}: IdentifiersSectionProps) => (
  <>
    <div className={style['section-header']}>
      <Text className={style['section-label']} element='span' textType={TextType.Label} value={messages.identifiersSectionLabel} />
      <div className={style['section-rule']} />
    </div>

    <FlexRow className={`${style['flex-row']} ${style['identifiers-row']}`} gap={Gap.Space16}>
      <FlexColumn className={style['flex-column']}>
        <InputLabel>
          <FormattedMessage {...messages.uomSkuLabel} />
        </InputLabel>
        <Input
          maxLength={UOM_SKU_MAX_LENGTH}
          onChange={(v: string) => onUomSkuChange(v.slice(0, UOM_SKU_MAX_LENGTH))}
          value={form.uomSku}
        />
        {isDuplicateUomSku && (
          <InlineMessage type={INLINE_MESSAGE_TYPES.warning}>
            <FormattedMessage {...messages.uomSkuDuplicate} />
          </InlineMessage>
        )}
        {form.uomSku.length >= UOM_SKU_MAX_LENGTH && (
          <InlineMessage type={INLINE_MESSAGE_TYPES.warning}>
            <FormattedMessage {...messages.uomSkuMaxLength} />
          </InlineMessage>
        )}
      </FlexColumn>

      <FlexColumn className={style['flex-column']}>
        <InputLabel hasError={!!barcodeError}>
          <FormattedMessage {...messages.barcodeLabel} />
        </InputLabel>
        <BarcodeField
          errorType={barcodeError}
          hasError={!!barcodeError}
          isAuto={form.barcodeIsAuto}
          isDuplicate={isDuplicateBarcode}
          onBlur={onBarcodeBlur}
          onChangeValue={onBarcodeChange}
          onRegenerate={onRegenerate}
          onToggleManual={onToggleManual}
          onUseAuto={onUseAuto}
          value={effectiveBarcode}
        />
      </FlexColumn>
    </FlexRow>
  </>
);
